/**
 * Unified analytics tracking layer.
 * Pushes events to both Mixpanel and GTM dataLayer.
 * All Mixpanel-specific code is isolated here; GTM push is via gtm.util.
 */

import mixpanel from 'mixpanel-browser'

import type {
  AnalyticsEventMap,
  AnalyticsEventName,
  AnalyticsSuperProperties,
} from '../interfaces/analytics.interface'
import type { GtmUserProperties } from '../interfaces/gtm.interface'
import { pushToDataLayer, setGtmUser } from './gtm.util'
import { logger } from './logger.util'
import { identifyClarityUser } from './pixel-clarity.util'
import { identifyMouseflowUser } from './pixel-mouseflow.util'

let isInitialized = false

export const initAnalytics = (token: string): void => {
  if (isInitialized) return
  if (!token) {
    logger.warn('[Analytics] No Mixpanel token provided, skipping initialization')
    return
  }

  const trackPageview = 'track_pageview'
  mixpanel.init(token, {
    [trackPageview]: false,
    persistence: 'localStorage',
  })
  isInitialized = true
  logger.debug('[Analytics] Mixpanel initialized')
}

export const identifyUser = (userId: string): void => {
  if (!isInitialized) return
  mixpanel.identify(userId)
  logger.debug('[Analytics] User identified', userId)
}

export const setSuperProperties = (properties: Partial<AnalyticsSuperProperties>): void => {
  if (!isInitialized) return
  mixpanel.register(properties)
}

export const setUserProfileProperties = (
  properties: Record<string, string | number | boolean | null>
): void => {
  if (!isInitialized) return
  mixpanel.people.set(properties)
}

export const trackEvent = <E extends AnalyticsEventName>(
  eventName: E,
  properties: AnalyticsEventMap[E]
): void => {
  const data = properties as Record<string, unknown>

  pushToDataLayer(eventName, data)

  if (isInitialized) {
    mixpanel.track(eventName, properties)
  }

  logger.debug('[Analytics] Event tracked', eventName, properties)
}

export const resetAnalytics = (): void => {
  if (!isInitialized) return
  mixpanel.reset()
  logger.debug('[Analytics] Mixpanel reset')
}

export const getIsAnalyticsInitialized = (): boolean => isInitialized

interface AnalyticsUserContext extends GtmUserProperties {
  email?: string
}

export const setAnalyticsUserContext = (properties: AnalyticsUserContext): void => {
  setGtmUser(properties)
  setSuperProperties({
    userId: properties.userId,
    orgId: properties.orgId,
    orgSlug: properties.orgSlug,
  })

  if (!properties.email) return

  identifyMouseflowUser(properties.email, properties.userId)
  identifyClarityUser(properties.email, properties.userId)
}
